import { useState, useEffect, useCallback, useRef } from 'react';

// ===========================|| HOOKS - LOCAL STORE ||=========================== //

// Instancias montadas por clave. El evento `storage` del navegador solo llega a
// otras pestañas, nunca a la que escribe, así que sin este registro dos
// componentes que usan la misma clave quedan con copias independientes y el que
// no escribió no vuelve a renderizar hasta recargar la página.
const subscribers = new Map<string, Set<(value: unknown) => void>>();

function subscribe(key: string, listener: (value: unknown) => void) {
  const listeners = subscribers.get(key) ?? new Set<(value: unknown) => void>();
  listeners.add(listener);
  subscribers.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) subscribers.delete(key);
  };
}

function broadcast(key: string, value: unknown, origin: (value: unknown) => void) {
  subscribers.get(key)?.forEach((listener) => {
    if (listener !== origin) listener(value);
  });
}

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState<ValueType>(() => {
    const storedValue = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
    if (storedValue === null) return defaultValue;
    try {
      return JSON.parse(storedValue) as ValueType;
    } catch {
      return defaultValue;
    }
  });

  // Espejo síncrono del valor: el setter lo necesita para resolver los updaters
  // funcionales sin provocar efectos secundarios dentro de un `setState`.
  const valueRef = useRef<ValueType>(value);

  // `setValue` y `valueRef` tienen identidad estable, así que este listener se
  // crea una sola vez y sirve además como identificador de la instancia.
  const listenerRef = useRef((next: unknown) => {
    valueRef.current = next as ValueType;
    setValue(next as ValueType);
  });

  useEffect(() => subscribe(key, listenerRef.current), [key]);

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (typeof window !== 'undefined' && e.storageArea === localStorage && e.key === key) {
        if (!e.newValue) {
          listenerRef.current(defaultValue);
          return;
        }
        try {
          listenerRef.current(JSON.parse(e.newValue));
        } catch {
          listenerRef.current(defaultValue);
        }
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = useCallback(
    (newValue: ValueType | ((prev: ValueType) => ValueType)) => {
      const result = typeof newValue === 'function' ? (newValue as (prev: ValueType) => ValueType)(valueRef.current) : newValue;
      valueRef.current = result;
      if (typeof window !== 'undefined') localStorage.setItem(key, JSON.stringify(result));
      setValue(result);
      broadcast(key, result, listenerRef.current);
    },
    [key]
  );

  return [value, setValueInLocalStorage] as const;
}
